import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Toolbar, IconButton, Tooltip, useMediaQuery } from '@mui/material';

// project import
import Drawer from './Drawer';
import Header from './Header';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';

// types
import { openDrawer } from 'store/reducers/menu';
import { drawerWidth, drawerWidthMin } from 'config';
// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
  const theme = useTheme();
  const isMapZoomed = useSelector((state) => state.zoom.isMapZoomed);
  const isPropertyInfoZoomed = useSelector((state) => state.zoom.isPropertyInfoZoomed);

  const matchDownLG = useMediaQuery(theme.breakpoints.down('lg'));
  const dispatch = useDispatch();

  const { drawerOpen } = useSelector((state) => state.menu);

  // drawer toggler
  const [open, setOpen] = useState(drawerOpen);
  const handleDrawerToggle = () => {
    setOpen(!open);
    dispatch(openDrawer({ drawerOpen: !open }));
  };

  useEffect(() => {
    // Initialize the drawer as closed on component mount
    setOpen(false);
    dispatch(openDrawer({ drawerOpen: false }));

    // If you still want to handle responsive behavior after the initial mount,
    // you can add additional logic here without overriding the initial state.
  }, []); // Empty dependency array ensures this runs only once on mount

  useEffect(() => {
    if (open !== drawerOpen) setOpen(drawerOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen]);

  // Hide drawer when isMapZoomed or isPropertyInfoZoomed is true
  const shouldHideDrawer = isMapZoomed || isPropertyInfoZoomed;

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      {matchDownLG && <Header open={open} handleDrawerToggle={handleDrawerToggle} />}
      {!shouldHideDrawer && <Drawer open={open} handleDrawerToggle={handleDrawerToggle} />}
      {!matchDownLG && !shouldHideDrawer && (
        <Tooltip title={drawerOpen ? 'Collapse' : 'Expand'}>
          <IconButton
            disableRipple
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            edge="start"
            color="secondary"
            sx={{
              color: 'text.primary',
              width: 30,
              height: 30,
              borderRadius: '50%',
              borderColor: theme.palette.primary.light,
              borderWidth: '2px',
              borderStyle: 'solid',
              bgcolor: theme.palette.secondary.light,
              position: 'fixed',
              top: 50,
              left: open ? drawerWidth : drawerWidthMin, // Adjust these values based on your drawer width
              transform: 'translateX(-20%)',
              transition: theme.transitions.create(['left'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.shorter
              }),
              zIndex: 1300
            }}
          >
            {!open ? <KeyboardArrowRightIcon /> : <KeyboardArrowLeftIcon />}
          </IconButton>
        </Tooltip>
      )}
      <Box
        component="main"
        sx={{
          width: `calc(100% - ${drawerWidth}px)`,
          flexGrow: 1,
          pl: { xs: isMapZoomed ? 0 : 2, sm: isMapZoomed ? 0 : 2 },
          pr: { xs: isMapZoomed ? 0 : 2, sm: isMapZoomed ? 0 : 1 },
          pt: { xs: 1, sm: 1 },
          pb: { xs: 0, sm: 0 }
        }}
      >
        {matchDownLG && <Toolbar />}
        <Outlet />
      </Box>
    </Box>
  );
};

export default MainLayout;
